// src/views/admin/Orders/OrderTable.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Text,
  Flex,
  Input,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  SimpleGrid,
  useBreakpointValue,
  FormControl,
  InputGroup,
  InputLeftElement,
  Select as ChakraSelect,
  Stack,
  FormLabel,
} from '@chakra-ui/react';

import {
  MdEdit,
  MdOutlineFilterAltOff,
  MdSearch,
} from 'react-icons/md';

import Card from 'components/card/Card';
import { useTranslation } from "react-i18next";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { IoMdEye } from 'react-icons/io';
import { useOrders } from "contexts/OrdersContext";

const OrderTable = () => {
  const { t } = useTranslation();
  const pageId = 14;
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // SOCKET CONTEXT
  const { liveOrders, clearLiveOrders } = useOrders();

  // State declarations for orders and filtering
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editableOrder, setEditableOrder] = useState(null);
  const [selectedOrder, setSelectedOrderId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [order, setOrder] = useState({});
  const [filterStatus, setFilterStatus] = useState("");
  const [filterClientName, setFilterClientName] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [permissions, setPermissions] = useState({
    canList: true,
    canEdit: true,
    canView: true,
    canInsert: true,
  });

  // Highlight new orders from socket
  const [highlightedOrderIds, setHighlightedOrderIds] = useState([]);

  // -----------------------------
  // PERMISSIONS
  // -----------------------------
  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: "No permissions found",
          description: "You don't have the required access.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      const permission = role.permissions.find((p) => p.page_id === pageId);
      if (permission) {
        setPermissions({
          canEdit: permission.update_access || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error loading permissions",
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, [toast]);

  // -----------------------------
  // 🔥 NEW ORDERS FROM SOCKET
  // -----------------------------
  useEffect(() => {
    if (!liveOrders.length) return;

    // Normalize and prepend new orders (avoid duplicates by id)
    setOrders(prev => {
      const existingIds = new Set(prev.map(o => o.id));
      const toAdd = liveOrders.filter(o => !existingIds.has(o.id));
      return [...toAdd, ...prev];
    });

    setFilteredOrders(prev => {
      const existingIds = new Set(prev.map(o => o.id));
      const toAdd = liveOrders.filter(o => !existingIds.has(o.id));
      return [...toAdd, ...prev];
    });

    // highlight new ones using id
    setHighlightedOrderIds(prev => {
      const newIds = liveOrders.map(o => o.id);
      return Array.from(new Set([...prev, ...newIds]));
    });

    // Extract order codes for toast
    const codes = liveOrders.map(o => o.order_code).filter(Boolean);

    const description =
      codes.length === 1
        ? `Order ${codes[0]} has arrived.`
        : `New orders received:\n${codes.join(" , ")}`;

    toast({
      title:
        codes.length === 1
          ? "🚨 New Order Received!"
          : `🚨 ${codes.length} New Orders Received!`,
      description,
      status: "success",
      position: "top",
      duration: 9000,
      isClosable: true,
      containerStyle: {
        fontSize: "1.1rem",
        fontWeight: "bold",
        background: "#f6e05e",
        color: "#2d3748",
        border: "2px solid #ecc94b",
        boxShadow: "0 0 12px #ecc94b",
        padding: "16px",
        whiteSpace: "pre-line",
      },
    });

    // clear the buffer in context
    clearLiveOrders();
  }, [liveOrders, toast, clearLiveOrders]);

  // remove highlight after 5 seconds
  useEffect(() => {
    if (!highlightedOrderIds.length) return;

    const timers = highlightedOrderIds.map(id =>
      setTimeout(() => {
        setHighlightedOrderIds(prev => prev.filter(x => x !== id));
      }, 5000)
    );

    return () => timers.forEach(clearTimeout);
  }, [highlightedOrderIds]);

  // -----------------------------
  // FETCH FILTERED ORDERS (API)
  // -----------------------------
  const fetchFilteredOrders = useCallback(
    async (pageNum) => {
      try {
        let url = `${process.env.REACT_APP_API_URL}/orders/order/filter`;
        const params = new URLSearchParams();
        params.append("page", pageNum);
        params.append("limit", ordersPerPage);
        if (filterStatus) params.append("status", filterStatus);
        if (filterClientName) params.append("client_name", filterClientName);
        if (filterFrom) params.append("from", filterFrom);
        if (filterTo) params.append("to", filterTo);
        params.append("page_id", pageId);
        url += "?" + params.toString();

        const response = await fetchWithToken(url, "GET");

        if (response.status === "success") {
          let ordersArray = [];

          if (Array.isArray(response.data.orders)) {
            ordersArray = response.data.orders;
          } else if (response.data.orders?.orders && typeof response.data.orders.orders === "object") {
            ordersArray = Object.values(response.data.orders.orders);
          } else if (typeof response.data.orders === "object") {
            ordersArray = Object.values(response.data.orders);
          }

          // 🔄 Normalize id field so API + Socket are consistent
          const normalized = ordersArray.map(o => ({
            ...o,
            id: o.id ?? o.order_id,
          }));

          setOrders(normalized);
          setFilteredOrders(normalized);
          setTotalPages(response.data.pagination?.total_pages || 1);
        } else {
          throw new Error("Failed to apply filters");
        }
      } catch (error) {
        console.error("Filter error:", error);
        toast({
          title: t("Error applying filters."),
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    },
    [ordersPerPage, filterStatus, filterClientName, filterFrom, filterTo, pageId, toast, t]
  );

  // Re-fetch orders whenever filters or pagination change
  useEffect(() => {
    fetchFilteredOrders(currentPage);
  }, [currentPage, filterStatus, filterClientName, filterFrom, filterTo, ordersPerPage, fetchFilteredOrders]);

  // -----------------------------
  // FETCH SINGLE ORDER BY ID (MODAL)
  // -----------------------------
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        if (!selectedOrder) return;
        const url = `${process.env.REACT_APP_API_URL}/branch/orders/order?order_id=${selectedOrder}&page_id=${pageId}`;
        const response = await fetchWithToken(url, "GET");
        if (response.status === "success") {
          setOrder(response.data);
        } else {
          throw new Error("Failed to fetch order");
        }
      } catch (error) {
        console.error("Fetch error:", error);
        toast({
          title: "Error fetching order.",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    };
    fetchOrder();
  }, [selectedOrder, t, toast]);

  // -----------------------------
  // SEARCH (local - optional)
  // -----------------------------
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  // -----------------------------
  // FETCH SPECIFIC ORDER FOR EDIT
  // -----------------------------
  const fetchSpecificOrder = async (orderId, branch_id) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/orders/order?order_id=${orderId}&page_id=${pageId}&branch_id=${branch_id}`;
      const response = await fetchWithToken(url, "GET");
      if (response.status === "success") {
        return response.data;
      } else {
        throw new Error("Failed to fetch order details");
      }
    } catch (error) {
      console.error("Error fetching specific order:", error);
      toast({
        title: t("Error fetching order details."),
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return null;
    }
  };

  // -----------------------------
  // EDIT ORDER
  // -----------------------------
  const handleEditOrder = async (order) => {
    if (!order.id) return;
    console.log(order);

    if (!order.branch_id) {
      toast({
        title: t("Cannot edit this order yet."),
        description: t("Branch id is missing."),
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    const orderDetails = await fetchSpecificOrder(order.id, order.branch_id);
    const data = { ...orderDetails, order_id: order.id };
    if (data) {
      setEditableOrder(data);
    }
  };

  // -----------------------------
  // APPLY FILTER BUTTON
  // -----------------------------
  const handleFilter = async () => {
    setCurrentPage(1);
    await fetchFilteredOrders(1);
  };

  // -----------------------------
  // SAVE EDITED ORDER (if needed)
  // -----------------------------
  const handleSaveOrder = async () => {
    try {
      const data = {
        order_id: editableOrder.order_id,
        branch_id: editableOrder.branch_id,
        delivery_id: editableOrder.delivery_id,
      };
      setIsSubmitting(true);
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/orders/order/update?page_id=${pageId}`,
        "PATCH",
        data
      );
      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.order_id === editableOrder.order_id
            ? { ...o, order_status: editableOrder.order_status, services: editableOrder.services }
            : o
        )
      );
      setFilteredOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.order_id === editableOrder.order_id
            ? { ...o, order_status: editableOrder.order_status, services: editableOrder.services }
            : o
        )
      );
      setEditableOrder(null);
      setIsSubmitting(false);
      toast({
        title: t("Order updated successfully."),
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Error updating order:", error);
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to update order."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      setIsSubmitting(false);
    }
  };

  // -----------------------------
  // FILTER FORM
  // -----------------------------
  const renderFilterForm = () => (
    <Stack
      direction={{ base: "column", md: "row" }}
      spacing={4}
      w="full"
      align="center"
      justify="right"
      mb={4}
      px={{ base: "10px", md: "25px" }}
    >
      {/* Client Name Search */}
      <FormControl maxW={{ base: "100%", md: "200px" }}>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <MdSearch color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={t("Client Name")}
            value={filterClientName}
            onChange={(e) => setFilterClientName(e.target.value)}
            size="sm"
            focusBorderColor="blue.400"
            _hover={{
              borderColor: "blue.400",
              boxShadow: "0 0 0 1px #3182CE",
              color: "blue.400",
              backgroundColor: "blue.50",
            }}
          />
        </InputGroup>
      </FormControl>

      {/* Status Filter */}
      <FormControl maxW={{ base: "100%", md: "180px" }}>
        <ChakraSelect
          placeholder={t("Select a status")}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          size="sm"
          focusBorderColor="blue.400"
          borderRadius="md"
          _hover={{
            borderColor: "blue.400",
            boxShadow: "0 0 0 1px #3182CE",
            color: "blue.400",
            backgroundColor: "blue.50",
          }}
        >
          <option value="completed">{t("completed")}</option>
          <option value="acceptance_pending">{t("pending")}</option>
          <option value="rejected">{t("rejected")}</option>
          <option value="accepted">{t("accepted")}</option>
          <option value="ready_to_pay">{t("ready to pay")}</option>
          <option value="cancelled">{t("cancelled")}</option>
        </ChakraSelect>
      </FormControl>

      {/* From Date */}
      <FormControl maxW={{ base: "100%", md: "180px" }}>
        <Input
          type="datetime-local"
          placeholder={t("From")}
          value={filterFrom}
          onChange={(e) => setFilterFrom(e.target.value)}
          size="sm"
          focusBorderColor="blue.400"
          _hover={{
            borderColor: "blue.400",
            boxShadow: "0 0 0 1px #3182CE",
            color: "blue.400",
            backgroundColor: "blue.50",
          }}
        />
      </FormControl>

      {/* To Date */}
      <FormControl maxW={{ base: "100%", md: "180px" }}>
        <Input
          type="datetime-local"
          placeholder={t("To")}
          value={filterTo}
          onChange={(e) => setFilterTo(e.target.value)}
          size="sm"
          focusBorderColor="blue.400"
          _hover={{
            borderColor: "blue.400",
            boxShadow: "0 0 0 1px #3182CE",
            color: "blue.400",
            backgroundColor: "blue.50",
          }}
        />
      </FormControl>

      {/* Orders Per Page */}
      <FormControl maxW={{ base: "100%", md: "120px" }}>
        <ChakraSelect
          value={ordersPerPage}
          onChange={(e) => {
            setOrdersPerPage(parseInt(e.target.value, 10));
            setCurrentPage(1);
          }}
          size="sm"
          focusBorderColor="blue.400"
          borderRadius="md"
          _hover={{
            borderColor: "blue.400",
            boxShadow: "0 0 0 1px #3182CE",
            color: "blue.400",
            backgroundColor: "blue.50",
          }}
        >
          <option value="10">10 {t("per page")}</option>
          <option value="20">20 {t("per page")}</option>
          <option value="50">50 {t("per page")}</option>
        </ChakraSelect>
      </FormControl>

      {/* Reset Filters Button */}
      <Button
        leftIcon={<MdOutlineFilterAltOff />}
        variant="outline"
        colorScheme="gray"
        size="sm"
        onClick={() => {
          setFilterStatus("");
          setFilterClientName("");
          setFilterFrom("");
          setFilterTo("");
          setCurrentPage(1);
          fetchFilteredOrders(1);
        }}
        _hover={{
          borderColor: "red.500",
          color: "red.500",
          backgroundColor: "rgba(245, 101, 101, 0.1)",
          boxShadow: "0 0 0 2px #F56565",
        }}
      >
        {t("Reset")}
      </Button>
    </Stack>
  );

  // -----------------------------
  // DESKTOP TABLE
  // -----------------------------
  const renderDesktopTable = () => {
    if (!filteredOrders || filteredOrders.length === 0) {
      return (
        <Box p="20px" textAlign="center">
          <Text fontSize="xl" color="gray.500">
            {t("No orders found")}
          </Text>
        </Box>
      );
    }

    return (
      <Box px="20px">
        <Box overflowY="auto" maxHeight="400px" px="20px">
          <Table variant="striped" colorScheme="gray">
            <Thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <Tr bg="rgb(69, 50, 240)" textColor="white">
                <Th color="white">{t("Index")}</Th>
                <Th color="white">{t("Branch")}</Th>
                <Th color="white">{t("Client")}</Th>
                <Th color="white">{t("Status")}</Th>
                <Th color="white">{t("Total Price")}</Th>
                <Th color="white">{t("Date")}</Th>
                <Th color="white">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredOrders.map((o, index) => (
                <Tr
                  key={o.id || o.order_id}
                  bg={highlightedOrderIds.includes(o.id) ? "yellow.100" : "transparent"}
                  transition="0.3s"
                >
                  <Td>{(currentPage - 1) * ordersPerPage + index + 1}</Td>
                  <Td>{o.branch_name}</Td>
                  <Td>{o.client_name}</Td>
                  <Td>{o.order_status} {o.status}</Td>
                  <Td>{`€${o.total_price}`}</Td>
                  <Td>{o.created_at ? new Date(o.created_at).toLocaleDateString() : "-"}</Td>
                  <Td>
                    <Flex>
                      <Tooltip label={t("View Order")}>
                        <Button
                          colorScheme="cyan"
                          color="white"
                          size="sm"
                          mr="2"
                          onClick={() => handleEditOrder(o)}
                          borderRadius="full"
                          boxShadow="md"
                          _hover={{ bg: "cyan.600", transform: "scale(1.05)" }}
                          _active={{ bg: "cyan.700", transform: "scale(0.95)" }}
                          leftIcon={<IoMdEye />}
                        >
                          {t("View")}
                        </Button>
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination Controls */}
        {totalPages > 0 && (
          <Box mt={4} mb={4}>
            <Text textAlign="center" fontSize="sm" color="gray.600" mb={3}>
              {t("Page")} {currentPage} {t("of")} {totalPages}
              {filteredOrders.length > 0 && (
                <span>
                  {" "}
                  - {t("Showing")} {(currentPage - 1) * ordersPerPage + 1} {t("to")}{" "}
                  {Math.min(currentPage * ordersPerPage, (currentPage - 1) * ordersPerPage + filteredOrders.length)}{" "}
                  {t("results")}
                </span>
              )}
            </Text>

            <Flex justifyContent="center" wrap="wrap" gap={2}>
              <Button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                size="sm"
                variant="outline"
              >
                {t("First")}
              </Button>

              <Button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                size="sm"
                colorScheme="cyan"
                variant="outline"
              >
                {t("Previous")}
              </Button>

              {(() => {
                const delta = 2;
                const range = [];
                const rangeWithDots = [];

                for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
                  range.push(i);
                }

                if (currentPage - delta > 2) {
                  rangeWithDots.push(1, '...');
                } else {
                  rangeWithDots.push(1);
                }

                rangeWithDots.push(...range);

                if (currentPage + delta < totalPages - 1) {
                  rangeWithDots.push('...', totalPages);
                } else if (totalPages > 1) {
                  rangeWithDots.push(totalPages);
                }

                return rangeWithDots.map((p, index) => {
                  if (p === '...') {
                    return (
                      <Text key={`ellipsis-${index}`} mx={2} alignSelf="center" fontSize="sm" color="gray.500">
                        ...
                      </Text>
                    );
                  }

                  return (
                    <Button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      variant={currentPage === p ? "solid" : "outline"}
                      colorScheme={currentPage === p ? "cyan" : "gray"}
                      color="blue.600"
                      size="sm"
                      minW="40px"
                    >
                      {p}
                    </Button>
                  );
                });
              })()}

              <Button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || filteredOrders.length === 0}
                size="sm"
                colorScheme="cyan"
                variant="outline"
              >
                {t("Next")}
              </Button>

              <Button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                size="sm"
                variant="outline"
              >
                {t("Last")}
              </Button>
            </Flex>
          </Box>
        )}
      </Box>
    );
  };

  // -----------------------------
  // MOBILE VIEW
  // -----------------------------
  const renderMobileView = () => {
    if (!filteredOrders || filteredOrders.length === 0) {
      return (
        <Box p="20px" textAlign="center">
          <Text fontSize="xl" color="gray.500">
            {currentPage === totalPages
              ? t("No more orders on this page.")
              : t("No orders found")}
          </Text>
        </Box>
      );
    }

    return (
      <SimpleGrid columns={{ base: 1 }} spacing={4} px="10px">
        {filteredOrders.map((o) => (
          <Card
            key={o.id || o.order_id}
            p="4"
            borderRadius="md"
            shadow="md"
            bg={highlightedOrderIds.includes(o.id) ? "yellow.100" : "white"}
            border={highlightedOrderIds.includes(o.id) ? "1px solid #ECC94B" : "1px solid #E2E8F0"}
          >
            <Box mb="3">
              <Text fontSize="sm" color="gray.500">
                {t("Order ID")}: {o.order_id || o.id}
              </Text>
              {o.branch_name && (
                <Text fontSize="sm" color="gray.500">
                  {o.branch_name}
                </Text>
              )}
              <Text fontSize="sm" color="gray.500">
                {t("Client")}: {o.client_name}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm">
                <b>{t("Status")}: </b> {o.order_status} {o.status}
              </Text>
              <Text fontSize="sm">
                <b>{t("Total Price")}: </b> €{o.total_price}
              </Text>
              <Flex mt="3" wrap="wrap">
                <Tooltip label={t("View Order")}>
                  <Button
                    colorScheme="cyan"
                    color="white"
                    isDisabled={!permissions.canEdit}
                    size="xs"
                    mr="2"
                    onClick={() => handleEditOrder(o)}
                  >
                    <MdEdit />
                  </Button>
                </Tooltip>
              </Flex>
            </Box>
          </Card>
        ))}

        {/* Pagination (mobile) */}
        <Box mt={6} mb={4}>
          <Flex justifyContent="center" mb={3}>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              {t("Page")} {currentPage} {t("of")} {totalPages || 1}
            </Text>
          </Flex>

          {totalPages > 1 && (
            <Flex justifyContent="center" wrap="wrap" gap={2}>
              <Button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                size="sm"
                colorScheme="cyan"
                variant={currentPage === 1 ? "outline" : "solid"}
              >
                {t("Previous")}
              </Button>

              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                let p;
                if (totalPages <= 3) {
                  p = i + 1;
                } else if (currentPage <= 2) {
                  p = i + 1;
                } else if (currentPage >= totalPages - 1) {
                  p = totalPages - 2 + i;
                } else {
                  p = currentPage - 1 + i;
                }

                return (
                  <Button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    variant={currentPage === p ? "solid" : "outline"}
                    colorScheme="cyan"
                    size="sm"
                    minW="40px"
                  >
                    {p}
                  </Button>
                );
              })}

              <Button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                size="sm"
                colorScheme="cyan"
                variant={currentPage === totalPages ? "outline" : "solid"}
              >
                {t("Next")}
              </Button>
            </Flex>
          )}
        </Box>
      </SimpleGrid>
    );
  };

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <Card
      direction="column"
      w="100%"
      px="0px"
      overflowX={{ base: "scroll", lg: "hidden" }}
      boxShadow="lg"
      bg="white"
    >
      {/* Header + Filters */}
      <Flex
        px={{ base: "10px", md: "25px" }}
        justify="space-between"
        mb="20px"
        align="center"
        bg="gray.100"
        py="10px"
        borderRadius="md"
      >
        <Text color="black" fontSize="22px" fontWeight="700" lineHeight="100%">
          {t("Orders")}
        </Text>
        {renderFilterForm()}
      </Flex>

      {isMobile ? renderMobileView() : renderDesktopTable()}

      {/* VIEW ORDER MODAL (detailed) */}
      {editableOrder && (
        <Modal isOpen={true} onClose={() => setEditableOrder(null)} size="4xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t("Order Details")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {/* General Info */}
              <Box mb={4}>
                <Text fontSize="lg" fontWeight="bold">
                  {t("General Information")}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={2}>
                  <Box>
                    <FormLabel color="blue.600">{t("Order Code")}</FormLabel>
                    <Text>{editableOrder.laundry_order_code}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Status")}</FormLabel>
                    <Text textTransform="capitalize">{editableOrder.status}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Order Type")}</FormLabel>
                    <Text>{editableOrder.order_type}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Total Price")}</FormLabel>
                    <Text>
                      {editableOrder.total_price} {editableOrder.currency}
                    </Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Collection Date")}</FormLabel>
                    <Text>{editableOrder.collection_date}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Delivery Date")}</FormLabel>
                    <Text>{editableOrder.delivery_date}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Extra Fee")}</FormLabel>
                    <Text>{editableOrder.extra_fee}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Refund Fee")}</FormLabel>
                    <Text>{editableOrder.refund_fee}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Service Fee")}</FormLabel>
                    <Text>{editableOrder.service_fee}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Sub Total")}</FormLabel>
                    <Text>{editableOrder.sub_total}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Delivery Fee")}</FormLabel>
                    <Text>{editableOrder.delivery_fee}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Client Note")}</FormLabel>
                    <Text>{editableOrder.client_note || "-"}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Branch Note")}</FormLabel>
                    <Text>{editableOrder.branch_note || "-"}</Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Client Info */}
              <Box mb={4}>
                <Text fontSize="lg" fontWeight="bold">
                  {t("Client Information")}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={2}>
                  <Box>
                    <FormLabel color="blue.600">{t("Name")}</FormLabel>
                    <Text>{editableOrder.client_name}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Email")}</FormLabel>
                    <Text>{editableOrder.client_email}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Phone")}</FormLabel>
                    <Text>{editableOrder.client_phone_number}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Address")}</FormLabel>
                    <Text>
                      {editableOrder.client_street_name}, {editableOrder.client_country_code},{" "}
                      {editableOrder.client_zip_code}
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Branch Info */}
              <Box mb={4}>
                <Text fontSize="lg" fontWeight="bold">
                  {t("Branch")}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={2}>
                  <Box>
                    <FormLabel color="blue.600">{t("Branch Name")}</FormLabel>
                    <Text>{editableOrder.branch_name}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Branch Logo")}</FormLabel>
                    {editableOrder.branch_logo && (
                      <img
                        src={editableOrder.branch_logo}
                        alt="branch logo"
                        style={{ maxWidth: "100px", borderRadius: "8px" }}
                      />
                    )}
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Interaction & Appointment */}
              <Box mb={4}>
                <Text fontSize="lg" fontWeight="bold">
                  {t("Appointment & Interaction")}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={2}>
                  <Box>
                    <FormLabel color="blue.600">{t("Interaction Type")}</FormLabel>
                    <Text>{editableOrder.interaction_type}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Collection Time Slot")}</FormLabel>
                    <Text>{editableOrder.collection_time_slot}</Text>
                  </Box>
                  <Box>
                    <FormLabel color="blue.600">{t("Delivery Time Slot")}</FormLabel>
                    <Text>{editableOrder.deliver_time_slot}</Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Items & Services */}
              {editableOrder.items && editableOrder.items.length > 0 && (
                <Box mb={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    {t("Items")}
                  </Text>
                  {editableOrder.items.map((item) => (
                    <Box key={item.id} borderWidth="1px" borderRadius="md" p={3} mt={2}>
                      <FormLabel color="blue.600">
                        {item.item_name} - {item.item_sku}
                      </FormLabel>
                      {item.item_image && (
                        <img
                          src={item.item_image}
                          alt={item.item_name}
                          style={{
                            maxWidth: "80px",
                            borderRadius: "6px",
                            marginBottom: 8,
                          }}
                        />
                      )}
                      <Text>
                        {t("Quantity")}: {item.quantity}
                      </Text>
                      <Text>
                        {t("Price")}: {item.price} {editableOrder.currency}
                      </Text>
                      {item.services && item.services.length > 0 && (
                        <Box mt={1}>
                          <FormLabel color="blue.600">{t("Services")}</FormLabel>
                          {item.services.map((s) => (
                            <Text key={s.id}>
                              - {s.sub_service_name} ({s.cost} {editableOrder.currency})
                            </Text>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </ModalBody>

            <ModalFooter>
              <Button colorScheme="blue" onClick={() => setEditableOrder(null)}>
                {t("Close")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* OLD SIMPLE DETAILS MODAL */}
      {selectedOrder && (
        <Modal size="2xl" isOpen={true} onClose={() => setSelectedOrderId("")}>
          <ModalOverlay />
          <ModalContent borderRadius="md" p={4}>
            <ModalHeader color="black" borderRadius="md">
              Order Details
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Stack spacing={4}>
                <Box p={4} borderWidth="1px" borderRadius="md" boxShadow="sm">
                  <SimpleGrid columns={2} spacing={4}>
                    <Text>
                      <strong>Order:</strong> {order.id}
                    </Text>
                    <Text>
                      <strong>Client:</strong> {order.client_id}
                    </Text>
                    {!order.uc_delivery && (
                      <Text>
                        <strong>Delivery:</strong> {order.delivery_id}
                      </Text>
                    )}
                    <Text>
                      <strong>Total Price:</strong> ${order.total_price}
                    </Text>
                    <Text>
                      <strong>Appointment Time:</strong> {order.appointment_time}
                    </Text>
                    <Text>
                      <strong>Status:</strong> {order.status}
                    </Text>
                    {order.pet_id && (
                      <Text>
                        <strong>Pet:</strong> {order.pet_id}
                      </Text>
                    )}
                    {order.car_id && (
                      <Text>
                        <strong>Car:</strong> {order.car_id}
                      </Text>
                    )}
                    <Text>
                      <strong>Interaction Type:</strong> {order.interaction_type}
                    </Text>
                    <Text>
                      <strong>Service Category:</strong> {order.service_category}
                    </Text>
                    <Text>
                      <strong>Cleaner Count:</strong> {order.cleaner_count}
                    </Text>
                    <Text>
                      <strong>With Equipment:</strong> {order.with_equipment ? "Yes" : "No"}
                    </Text>
                    <Text>
                      <strong>Start Time:</strong> {order.start_time}
                    </Text>
                    <Text>
                      <strong>End Time:</strong> {order.end_time}
                    </Text>
                  </SimpleGrid>
                </Box>
              </Stack>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Card>
  );
};

export default OrderTable;
